import { apiRequest } from "@/lib/api";
import { assignHardwareCostBands, hardwareCostBandLabel } from "@/lib/hardware-cost-band";

export type ProviderRegistryInputField = {
  key: string;
  label: string;
  type: string;
  required: boolean;
  secret: boolean;
  options_source?: string;
  default?: unknown;
};

export type ProviderRegistryCredentialMode = {
  key: string;
  surfaces: string[];
  fields: string[];
};

export type ProviderRegistryAccessMode = {
  supported: boolean;
  support_mode?: string;
  inputs?: string[];
  outputs?: string[];
  actions?: string[];
  requires_public_ip?: boolean;
  supports_runtime_agent?: boolean;
  notes?: string[];
};

export type ProviderRegistryManagedWorkloads = {
  supported: boolean;
  runtime_protocol?: string;
  workload_types?: string[];
  session_modes?: string[];
  supports_input_artifacts?: boolean;
  supports_custom_models?: boolean;
  supports_run_snapshots?: boolean;
  supports_snapshot_restore?: boolean;
};

export type ProviderRegistryDescriptor = {
  api_version: string;
  slug: string;
  aliases: string[];
  display_name: string;
  resource: string;
  documentation?: string;
  credentials: {
    modes: string[];
    mode_specs: ProviderRegistryCredentialMode[];
    default_mode?: string;
    fields: ProviderRegistryInputField[];
    oauth_scopes?: string[];
  };
  account_inputs: ProviderRegistryInputField[];
  launch_inputs: ProviderRegistryInputField[];
  capabilities: {
    provider_slug: string;
    catalog: boolean;
    launch: boolean;
    list: boolean;
    terminate: boolean;
    actions: string[];
    access_modes?: Record<string, ProviderRegistryAccessMode>;
    resource?: string;
    cleanup_finalizer?: boolean;
    normalized_catalog?: boolean;
    launch_planning?: boolean;
    runtime_provider?: boolean;
    managed_workloads?: ProviderRegistryManagedWorkloads;
    retry_safe_actions?: boolean;
    retry_safe_cleanup?: boolean;
  };
  products: {
    wallet_direct: ProviderRegistryProduct;
    pro: ProviderRegistryProduct & { credential_modes?: string[] };
    managed_workload?: ProviderRegistryProduct;
    persistent_session?: ProviderRegistryProduct;
    run_snapshot?: ProviderRegistryProduct;
    marketplace?: ProviderRegistryProduct;
    spoke?: ProviderRegistryProduct;
  };
};

export type ProviderRegistryProduct = {
  enabled: boolean;
  ready: boolean;
  modes?: string[];
  blockers?: string[];
};

export type ProviderDirectLaunchProduct = "wallet_direct" | "pro";

export type ProviderRegistryResponse = {
  api_version: string;
  count: number;
  providers: ProviderRegistryDescriptor[];
};

export type ProviderInputValue = string | number | boolean | string[];
export type ProviderInputValues = Record<string, ProviderInputValue>;

export type ProviderCatalogOption = {
  value: string;
  label: string;
  disabled?: boolean;
  detail?: string;
};

export type ProviderCostComponentState = {
  kind: string;
  billable: "yes" | "no" | "unknown";
};

export type ProviderRegistryOption = {
  value: string;
  label: string;
};

export type ProviderManagedWorkloadRequirements = {
  customModels?: boolean;
  inputArtifacts?: boolean;
  workloadType?: string;
  sessionMode?: string;
  runSnapshots?: boolean;
  snapshotRestore?: boolean;
};
export type ProviderProSetupOption = ProviderRegistryOption & {
  disabled?: boolean;
  hint?: string;
};

export type ProviderCredentialSecretStatus = {
  key: string;
  label: string;
  configured: boolean;
  version?: number;
  rotated_at?: string;
};

export type ProviderDirectCleanupState = "not_requested" | "pending" | "complete";

export function providerJobActionPayload(jobID: string, action: string) {
  return {
    id: String(jobID || "").trim(),
    action: String(action || "")
      .trim()
      .toLowerCase(),
  };
}

export async function readProviderRegistry(surface?: "pro" | "server") {
  const query = surface ? `?surface=${encodeURIComponent(surface)}` : "";
  return apiRequest<ProviderRegistryResponse>(`/api/providers/registry${query}`, {
    method: "GET",
    timeoutMs: 20_000,
    cache: "no-store",
  });
}

function providerLookupKey(value: string) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function providerDescriptorForSlug(
  providers: ProviderRegistryDescriptor[],
  providerSlug: string,
) {
  const lookup = providerLookupKey(providerSlug);
  if (!lookup) return undefined;
  return providers.find(
    (provider) =>
      providerLookupKey(provider.slug) === lookup ||
      (provider.aliases || []).some((alias) => providerLookupKey(alias) === lookup),
  );
}

export function providerProductAvailable(product?: ProviderRegistryProduct) {
  return Boolean(product?.enabled && product.ready);
}

export function providerProSetupOptions(
  providers: ProviderRegistryDescriptor[],
  seedOptions: ProviderProSetupOption[] = [],
): ProviderProSetupOption[] {
  const decorate = (
    option: ProviderProSetupOption,
    provider: ProviderRegistryDescriptor | undefined,
  ): ProviderProSetupOption => {
    if (!provider) return { ...option };
    const available = providerProductAvailable(provider.products.pro);
    const label = provider.display_name || option.label || provider.slug;
    if (available) return { ...option, value: provider.slug, label };
    return {
      ...option,
      value: provider.slug,
      label: `${label} — unavailable`,
      disabled: true,
      hint: provider.products.pro.blockers?.[0] || "Provider setup is unavailable",
    };
  };

  const options = seedOptions.map((option) =>
    decorate(option, providerDescriptorForSlug(providers, option.value)),
  );
  const represented = new Set(
    options
      .map((option) => providerDescriptorForSlug(providers, option.value)?.slug)
      .filter((slug): slug is string => Boolean(slug)),
  );
  for (const provider of providers) {
    if (represented.has(provider.slug)) continue;
    options.push(
      decorate(
        { value: provider.slug, label: provider.display_name || provider.slug },
        provider,
      ),
    );
    represented.add(provider.slug);
  }
  return options;
}

export function providerManagedWorkloadOptions(
  providers: ProviderRegistryDescriptor[],
  requirements: ProviderManagedWorkloadRequirements = {},
): ProviderRegistryOption[] {
  const seen = new Set<string>();
  return providers
    .filter(
      (provider) => providerSupportsManagedWorkloadRequest(provider, requirements),
    )
    .map((provider) => ({
      value: String(provider.slug || "").trim(),
      label: String(provider.display_name || provider.slug || "").trim(),
    }))
    .filter((option) => {
      const key = providerLookupKey(option.value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function providerSupportsManagedWorkloadRequest(
  provider: ProviderRegistryDescriptor | undefined,
  requirements: ProviderManagedWorkloadRequirements = {},
) {
  const managed = provider?.capabilities?.managed_workloads;
  if (
    !providerProductAvailable(provider?.products?.managed_workload) ||
    !provider?.capabilities?.runtime_provider ||
    !managed?.supported
  ) {
    return false;
  }
  if (requirements.customModels && !managed.supports_custom_models) return false;
  if (requirements.inputArtifacts && !managed.supports_input_artifacts) return false;
  const workloadType = providerLookupKey(requirements.workloadType || "");
  if (
    workloadType &&
    !(managed.workload_types || []).some(
      (candidate) => providerLookupKey(candidate) === workloadType,
    )
  ) {
    return false;
  }
  const sessionMode = providerLookupKey(requirements.sessionMode || "");
  if (
    sessionMode &&
    !(managed.session_modes || []).some(
      (candidate) => providerLookupKey(candidate) === sessionMode,
    )
  ) {
    return false;
  }
  if (
    sessionMode === "persistent" &&
    !providerProductAvailable(provider?.products?.persistent_session)
  ) {
    return false;
  }
  if (
    requirements.runSnapshots &&
    (!managed.supports_run_snapshots ||
      !providerProductAvailable(provider?.products?.run_snapshot))
  ) {
    return false;
  }
  if (
    requirements.snapshotRestore &&
    (!managed.supports_snapshot_restore ||
      !providerProductAvailable(provider?.products?.run_snapshot))
  ) {
    return false;
  }
  return true;
}

export function providerSupportsLegacyAPIKeySetup(provider?: ProviderRegistryDescriptor) {
  const modes = (provider?.credentials?.modes || []).map(providerLookupKey).filter(Boolean);
  return Boolean(
    providerProductAvailable(provider?.products?.pro) &&
      modes.length > 0 &&
      modes.every((mode) => mode === "api_key" || mode === "static_api_key"),
  );
}

export function providerDirectLaunchProductForAccount(
  accountID: string,
): ProviderDirectLaunchProduct {
  return String(accountID || "").trim().toLowerCase().startsWith("proconn_")
    ? "pro"
    : "wallet_direct";
}

export function providerDirectLaunchBlocker(
  provider: ProviderRegistryDescriptor | undefined,
  product: ProviderDirectLaunchProduct = "wallet_direct",
) {
  return provider?.products?.[product]?.blockers?.[0];
}

export function providerSupportsDirectLaunch(
  provider?: ProviderRegistryDescriptor,
  product: ProviderDirectLaunchProduct = "wallet_direct",
) {
  const accessModes = Object.values(provider?.capabilities?.access_modes || {});
  return Boolean(
    providerProductAvailable(provider?.products?.[product]) &&
      provider?.capabilities?.catalog &&
      provider?.capabilities?.launch &&
      (accessModes.length === 0 || accessModes.some((mode) => mode.supported)),
  );
}

export function providerSupportsProNetworkVisibility(
  provider?: ProviderRegistryDescriptor,
) {
  if (!provider) return false;
  // Legacy one-key Pro providers predate the registry product contract and
  // retain their existing network-supply path. Registry-contract providers
  // must explicitly pass ProductMarketplace eligibility.
  if (!String(provider.api_version || "").trim()) return true;
  return providerProductAvailable(provider.products?.marketplace);
}

export function providerLaunchIsRawVM(
  provider: ProviderRegistryDescriptor | undefined,
  values: ProviderInputValues = {},
) {
  const requestedMode = providerLookupKey(
    String(values.access_mode || values.pro_access_mode || ""),
  );
  if (requestedMode && requestedMode !== "vm") return false;

  const vmCapability = provider?.capabilities?.access_modes?.vm;
  return Boolean(
    vmCapability?.supported &&
      provider?.capabilities?.launch_planning,
  );
}

export function providerDirectLaunchIsModelFree(
  accountID: string,
  provider: ProviderRegistryDescriptor | undefined,
  values: ProviderInputValues = {},
) {
  return (
    providerDirectLaunchProductForAccount(accountID) === "pro" ||
    providerLaunchIsRawVM(provider, values)
  );
}

export function providerDirectProAccessMode(values: Record<string, unknown> = {}) {
  return String(values.pro_access_mode || values.access_mode || "vm").trim() || "vm";
}

export function providerDirectCleanupState(job: {
  status?: unknown;
  provider_status?: unknown;
} | null | undefined): ProviderDirectCleanupState {
  const status = providerLookupKey(String(job?.status || ""));
  const providerStatus = providerLookupKey(String(job?.provider_status || ""));
  if (
    status === "cleanup_pending" ||
    providerStatus === "genrl_cleanup_pending" ||
    providerStatus === "genrl_force_cleanup_pending" ||
    providerStatus.endsWith("_cleanup_pending")
  ) {
    return "pending";
  }
  if (
    status === "terminated" ||
    providerStatus === "terminated" ||
    providerStatus === "remote_not_found"
  ) {
    return "complete";
  }
  return "not_requested";
}

export function providerSupportsAction(
  provider: ProviderRegistryDescriptor | undefined,
  action: string,
) {
  const expected = providerLookupKey(action);
  if (!expected) return false;
  const actions = [
    ...(provider?.capabilities?.actions || []),
    ...Object.values(provider?.capabilities?.access_modes || {}).flatMap(
      (mode) => mode.actions || [],
    ),
  ];
  return actions.some((candidate) => providerLookupKey(candidate) === expected);
}

export function providerCredentialFieldsForMode(
  provider: ProviderRegistryDescriptor | undefined,
  mode: string,
) {
  if (!provider) return [];
  const selectedMode = provider.credentials.mode_specs.find(
    (candidate) => providerLookupKey(candidate.key) === providerLookupKey(mode),
  );
  const allowed = new Set((selectedMode?.fields || []).map(providerLookupKey));
  return provider.credentials.fields.filter((field) => allowed.has(providerLookupKey(field.key)));
}

export function providerInputInitialValues(
  fields: ProviderRegistryInputField[],
  current: Record<string, unknown> = {},
) {
  return fields.reduce<ProviderInputValues>((values, field) => {
    if (current[field.key] !== undefined) {
      values[field.key] = providerInputValue(field, current[field.key]);
    } else if (field.default !== undefined && field.default !== null) {
      values[field.key] = providerInputValue(field, field.default);
    } else {
      values[field.key] = field.type === "boolean"
        ? false
        : field.type === "integer"
          ? 0
          : providerInputIsList(field)
            ? []
            : "";
    }
    return values;
  }, {});
}

export function providerInputValue(field: ProviderRegistryInputField, raw: unknown): ProviderInputValue {
  if (field.type === "boolean") {
    if (typeof raw === "boolean") return raw;
    return ["1", "true", "yes", "on"].includes(String(raw || "").trim().toLowerCase());
  }
  if (field.type === "integer") {
    const value = Number(raw);
    return Number.isFinite(value) ? Math.round(value) : 0;
  }
  if (providerInputIsList(field)) {
    const values = Array.isArray(raw) ? raw : String(raw || "").split(/[\n,]/);
    return [...new Set(values.map((value) => String(value).trim()).filter(Boolean))];
  }
  if (
    String(field.type || "").trim().toLowerCase() === "json" &&
    raw !== null &&
    typeof raw === "object"
  ) {
    return JSON.stringify(raw, null, 2);
  }
  return String(raw ?? "").trim();
}

export function providerInputIsList(field: ProviderRegistryInputField) {
  return ["multi_select", "string_list"].includes(
    String(field.type || "").trim().toLowerCase(),
  );
}

export function providerInputUsesMultilineSecret(field: ProviderRegistryInputField) {
  const key = providerLookupKey(field.key);
  return Boolean(
    field.secret &&
      (String(field.type || "").trim().toLowerCase() === "secret_file_or_ref" ||
        key === "credential_json" ||
        key.endsWith("_json")),
  );
}

export function providerInputUsesMultilineEditor(field: ProviderRegistryInputField) {
  const type = String(field.type || "").trim().toLowerCase();
  return (
    providerInputUsesMultilineSecret(field) ||
    type === "json" ||
    type === "text" ||
    providerInputIsList(field)
  );
}

export function providerInvalidJSONInputs(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  return fields
    .filter((field) => {
      const key = providerLookupKey(field.key);
      const type = String(field.type || "").trim().toLowerCase();
      return type === "json" || key === "credential_json" || key.endsWith("_json");
    })
    .filter((field) => {
      const value = String(values[field.key] ?? "").trim();
      if (!value) return false;
      try {
        JSON.parse(value);
        return false;
      } catch {
        return true;
      }
    })
    .map((field) => field.label || field.key);
}

export function providerLiteralAllRegionInputs(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  return fields
    .filter(
      (field) =>
        providerInputIsList(field) && providerLookupKey(field.key).includes("region"),
    )
    .filter((field) => {
      const value = providerInputValue(field, values[field.key]);
      return Array.isArray(value) && value.some((item) => providerLookupKey(item) === "all");
    })
    .map((field) => field.label || field.key);
}

export function providerAccountScopeErrors(values: ProviderInputValues) {
  const relationships = [
    { list: "subscription_ids", selected: "subscription_id", label: "Launch subscription" },
    {
      list: "allowed_resource_groups",
      selected: "resource_group",
      label: "Resource group",
    },
    { list: "allowed_regions", selected: "default_region", label: "Default region" },
    { list: "allowed_zones", selected: "default_zone", label: "Default zone" },
  ];
  return relationships.flatMap(({ list, selected, label }) => {
    const selectedValue = String(values[selected] ?? "").trim();
    const rawList = values[list];
    const allowed = (Array.isArray(rawList) ? rawList : String(rawList ?? "").split(/[\n,]/))
      .map((value) => String(value).trim())
      .filter(Boolean);
    if (!selectedValue || allowed.length === 0) return [];
    return allowed.some((value) => value.toLowerCase() === selectedValue.toLowerCase())
      ? []
      : [`${label} must be included in ${list.replace(/_/g, " ")}`];
  });
}
export function providerInputDisplayValue(value: ProviderInputValue | undefined) {
  return Array.isArray(value) ? value.join(", ") : String(value ?? "");
}

export function providerMissingRequiredInputs(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  return fields
    .filter((field) => field.required && field.key !== "idempotency_key")
    .filter((field) => {
      const value = values[field.key];
      if (typeof value === "boolean") return false;
      if (typeof value === "number") return value <= 0;
      if (Array.isArray(value)) return value.length === 0;
      return String(value || "").trim() === "";
    })
    .map((field) => field.label || field.key);
}

export function providerCredentialInput(
  provider: ProviderRegistryDescriptor,
  mode: string,
  values: ProviderInputValues,
) {
  const properties: Record<string, string> = {};
  const secrets: Record<string, string | null> = {};
  for (const field of providerCredentialFieldsForMode(provider, mode)) {
    const value = providerInputValue(field, values[field.key]);
    const serialized = Array.isArray(value) ? value.join(",") : String(value ?? "").trim();
    if (!serialized) continue;
    if (field.secret) secrets[field.key] = serialized;
    else properties[field.key] = serialized;
  }
  return {
    mode,
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
    ...(Object.keys(secrets).length > 0 ? { secrets } : {}),
  };
}

export function providerTypedProSetupPayload(input: {
  provider: ProviderRegistryDescriptor;
  mode: string;
  credentialValues: ProviderInputValues;
  accountValues: ProviderInputValues;
  providerName?: string;
  displayName?: string;
}) {
  const providerName = String(
    input.providerName || input.provider.display_name || input.provider.slug,
  ).trim();
  return {
    provider_slug: input.provider.slug,
    provider_name: providerName,
    display_name: String(input.displayName || `${providerName} Provider`).trim(),
    auth: providerCredentialInput(input.provider, input.mode, input.credentialValues),
    // Credential material is accepted only through the selected auth contract.
    // A malformed descriptor must not copy a secret-marked account field into
    // the durable, readable account policy projection.
    account: providerAccountInputPayload(
      input.provider.account_inputs.filter((field) => !field.secret),
      input.accountValues,
    ),
    visibility: "private" as const,
  };
}

export function providerClearSecretInputValues(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  const secretKeys = new Set(
    fields.filter((field) => field.secret).map((field) => field.key),
  );
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !secretKeys.has(key)),
  ) as ProviderInputValues;
}

export function providerSupportsTypedCredentialRotation(
  provider: ProviderRegistryDescriptor | undefined,
  mode: string,
) {
  const normalizedMode = providerLookupKey(mode);
  if (!normalizedMode || normalizedMode === "api_key" || normalizedMode === "static_api_key") {
    return false;
  }
  const modeSpec = provider?.credentials.mode_specs.find(
    (candidate) => providerLookupKey(candidate.key) === normalizedMode,
  );
  return Boolean(modeSpec && providerCredentialFieldsForMode(provider, normalizedMode).length > 0);
}

const providerAccessModePreference = ["vm", "container", "notebook", "api"];

export function providerAccessModeOptions(
  provider: ProviderRegistryDescriptor | undefined,
): ProviderRegistryOption[] {
  return Object.entries(provider?.capabilities?.access_modes || {})
    .filter(([, capability]) => capability.supported)
    .map(([mode]) => ({ value: mode, label: providerTokenLabel(mode) }))
    .sort((left, right) => {
      const leftIndex = providerAccessModePreference.indexOf(providerLookupKey(left.value));
      const rightIndex = providerAccessModePreference.indexOf(providerLookupKey(right.value));
      const leftRank = leftIndex < 0 ? providerAccessModePreference.length : leftIndex;
      const rightRank = rightIndex < 0 ? providerAccessModePreference.length : rightIndex;
      return leftRank - rightRank || left.label.localeCompare(right.label);
    });
}

function providerTokenLabel(value: string) {
  return String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function providerCredentialRotationInput(
  provider: ProviderRegistryDescriptor | undefined,
  mode: string,
  values: ProviderInputValues,
) {
  if (!providerSupportsTypedCredentialRotation(provider, mode) || !provider) return undefined;
  const properties: Record<string, string> = {};
  const secrets: Record<string, string | null> = {};
  for (const field of providerCredentialFieldsForMode(provider, mode)) {
    if (!Object.prototype.hasOwnProperty.call(values, field.key)) continue;
    const value = providerInputValue(field, values[field.key]);
    const serialized = Array.isArray(value) ? value.join(",") : String(value ?? "").trim();
    if (!serialized) continue;
    if (field.secret) secrets[field.key] = serialized;
    else properties[field.key] = serialized;
  }
  if (Object.keys(properties).length === 0 && Object.keys(secrets).length === 0) return undefined;
  return {
    mode: providerLookupKey(mode),
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
    ...(Object.keys(secrets).length > 0 ? { secrets } : {}),
  };
}

export function providerCredentialSecretStatuses(
  provider: ProviderRegistryDescriptor | undefined,
  mode: string,
  credentialStatus: Record<string, unknown> | undefined,
): ProviderCredentialSecretStatus[] {
  return providerCredentialFieldsForMode(provider, mode)
    .filter((field) => field.secret)
    .map((field) => {
      const raw = credentialStatus?.[field.key];
      const status = raw && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : undefined;
      const version = Number(status?.version || 0);
      const rotatedAt = String(status?.rotated_at || "").trim();
      return {
        key: field.key,
        label: field.label || field.key,
        configured: status?.configured === true,
        ...(version > 0 ? { version } : {}),
        ...(rotatedAt ? { rotated_at: rotatedAt } : {}),
      };
    });
}

export function providerAccountInputPayload(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  const account: Record<string, unknown> = {};
  for (const field of fields) {
    const value = providerInputValue(field, values[field.key]);
    const empty =
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "number" && value === 0 && !field.required);
    if (!empty) {
      if (String(field.type || "").trim().toLowerCase() === "json" && typeof value === "string") {
        try {
          account[field.key] = JSON.parse(value);
          continue;
        } catch {
          // Validation reports malformed JSON before submission. Preserve the
          // original value here so direct helper callers do not lose input.
        }
      }
      account[field.key] = value;
    }
  }
  return account;
}

// Keep this list aligned with fields explicitly decoded by
// walletProviderJobLaunchRequest. Descriptor-only inputs must survive JSON
// decoding under provider_parameters instead of being flattened and dropped.
const canonicalLaunchKeys = new Set([
  "project_id",
  "subscription_id",
  "resource_group",
  "region",
  "zone",
  "shape_id",
  "image_id",
  "ssh_key_id",
  "network_id",
  "subnetwork_id",
  "network_profile_id",
  "boot_disk_gb",
  "boot_disk_type",
  "data_disk_gb",
  "data_disk_type",
  "spot",
  "spot_eviction_policy",
  "spot_max_price_decimal",
  "provisioning_mode",
  "capacity_reservation_id",
  "queue_timeout_seconds",
  "max_run_duration_seconds",
  "admin_username",
  "security_type",
  "secure_boot",
  "vtpm",
  "hibernation",
  "availability_set_id",
  "proximity_placement_group_id",
  "capacity_reservation_group_id",
  "dedicated_host_id",
  "vm_identity_resource_id",
  "assign_public_ip",
]);

const launchAccountDefaultKeys: Record<string, string[]> = {
  subscription_id: ["default_subscription_id", "subscription_ids"],
  resource_group: ["default_resource_group", "allowed_resource_groups"],
  region: ["default_region", "allowed_regions"],
  zone: ["default_zone", "allowed_zones"],
  image_id: ["default_image_id"],
  network_id: ["default_network_id"],
  network_profile_id: ["default_network_profile_id"],
  subnetwork_id: ["subnet_id", "default_subnetwork_id", "default_subnet_id"],
  boot_disk_gb: ["os_disk_gb"],
  boot_disk_type: ["os_disk_type"],
  admin_username: ["default_admin_username"],
  security_type: ["default_security_type"],
  secure_boot: ["default_secure_boot"],
  vtpm: ["default_vtpm"],
  assign_public_ip: ["public_ip", "default_assign_public_ip"],
};

function providerLaunchDefaultValue(raw: unknown, listInput: boolean) {
  if (raw === undefined || raw === null) return undefined;
  if (Array.isArray(raw)) {
    const values = raw.map((value) => String(value).trim()).filter(Boolean);
    return listInput ? values : values[0];
  }
  if (typeof raw === "string" && !raw.trim()) return undefined;
  return raw;
}

export function providerLaunchInputDefaults(
  fields: ProviderRegistryInputField[],
  requestBody: Record<string, unknown> | undefined,
) {
  const account = requestBody?.account;
  const accountValues = account && typeof account === "object" && !Array.isArray(account)
    ? (account as Record<string, unknown>)
    : undefined;
  const defaults = providerInputInitialValues(fields);
  if (!accountValues) return defaults;

  for (const field of fields) {
    if (field.secret || field.key === "idempotency_key") continue;
    const candidates = [field.key, ...(launchAccountDefaultKeys[field.key] || [])];
    for (const key of candidates) {
      if (!Object.prototype.hasOwnProperty.call(accountValues, key)) continue;
      const raw = providerLaunchDefaultValue(accountValues[key], providerInputIsList(field));
      if (raw === undefined) continue;
      defaults[field.key] = providerInputValue(field, raw);
      break;
    }
  }
  return defaults;
}

export function providerLaunchInputPayload(
  fields: ProviderRegistryInputField[],
  values: ProviderInputValues,
) {
  const payload: Record<string, unknown> = {};
  const providerParameters: Record<string, unknown> = {};
  const provisioningMode = String(values.provisioning_mode || "").trim();
  for (const field of fields) {
    if (field.key === "idempotency_key") continue;
    if (field.key === "capacity_reservation_id" && provisioningMode !== "reservation") continue;
    if (
      (field.key === "queue_timeout_seconds" || field.key === "max_run_duration_seconds") &&
      provisioningMode !== "flex_start"
    ) {
      continue;
    }
    const value = providerInputValue(field, values[field.key]);
    const empty =
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "number" && value === 0 && !field.required);
    if (empty) continue;
    if (field.key === "access_mode") {
      payload.pro_access_mode = value;
    } else if (canonicalLaunchKeys.has(field.key)) {
      payload[field.key] = value;
    } else {
      providerParameters[field.key] = value;
    }
  }
  if (Object.keys(providerParameters).length > 0) {
    payload.provider_parameters = providerParameters;
  }
  return payload;
}

export function providerDirectRawComputeLaunchPayload(input: {
  accountID: string;
  name?: string;
  providerInputs?: Record<string, unknown>;
  extraInputs?: Record<string, unknown>;
  launchIntent?: Record<string, unknown>;
}) {
  const providerInputs = input.providerInputs || {};
  return {
    account_id: String(input.accountID || "").trim(),
    name: String(input.name || "").trim(),
    workload_type: "raw_compute",
    pro_access_mode: providerDirectProAccessMode(providerInputs),
    wallet_route_type: "provider_direct",
    ...(input.extraInputs || {}),
    ...providerInputs,
    ...(input.launchIntent || {}),
  };
}

function rows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter((row): row is Record<string, unknown> => Boolean(row && typeof row === "object"))
    : [];
}

function textValue(row: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = String(row[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

export function providerCostComponentStates(value: unknown): ProviderCostComponentState[] {
  return rows(value).map((component) => {
    const raw = component.billable;
    const normalized = String(raw ?? "").trim().toLowerCase();
    const billable = raw === true || normalized === "yes"
      ? "yes"
      : raw === false || normalized === "no"
        ? "no"
        : "unknown";
    return {
      kind: textValue(component, "kind") || "resource",
      billable,
    };
  });
}

function uniqueOptions(options: ProviderCatalogOption[]) {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = String(option.value || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function acceleratorLabel(shape: Record<string, unknown>) {
  const accelerators = Array.isArray(shape.accelerators) ? shape.accelerators : [];
  const normalizedAccelerator = accelerators.find(
    (candidate) => candidate && typeof candidate === "object",
  );
  const accelerator =
    shape.accelerator && typeof shape.accelerator === "object"
      ? (shape.accelerator as Record<string, unknown>)
      : normalizedAccelerator && typeof normalizedAccelerator === "object"
        ? (normalizedAccelerator as Record<string, unknown>)
        : undefined;
  if (!accelerator) return "";
  const vendor = textValue(accelerator, "vendor");
  const rawModel = textValue(accelerator, "model");
  const model = rawModel.toLowerCase().startsWith(vendor.toLowerCase())
    ? rawModel
    : [vendor, rawModel].filter(Boolean).join(" ");
  const numerator = Number(accelerator.fraction_numerator || 0);
  const denominator = Number(accelerator.fraction_denominator || 0);
  const fraction = numerator > 0 && denominator > 0 ? `${numerator}/${denominator} GPU` : "";
  const count = Number(accelerator.device_count || shape.gpu_count || 0);
  const vram = Number(accelerator.vram_gb || 0);
  const countLabel = !fraction && count > 0 ? `${count} GPU${count === 1 ? "" : "s"}` : "";
  const memoryLabel =
    vram > 0
      ? count > 1 && !fraction
        ? `${vram} GB each · ${(vram * count).toLocaleString()} GB total VRAM`
        : `${vram} GB VRAM`
      : "";
  return [fraction || countLabel, model, memoryLabel].filter(Boolean).join(" · ");
}

function catalogShapeHourlyCost(shape: Record<string, unknown>) {
  const pricing = shape.pricing && typeof shape.pricing === "object" && !Array.isArray(shape.pricing)
    ? shape.pricing as Record<string, unknown>
    : {};
  const candidates = [
    shape.price_per_hour,
    shape.hourly_price,
    shape.cost_per_hour,
    shape.on_demand_price,
    shape.price,
    pricing.price_per_hour,
    pricing.hourly_price,
    pricing.hourly,
    pricing.on_demand,
    pricing.price,
  ].map(Number);
  return candidates.find((value) => Number.isFinite(value) && value > 0);
}

function catalogShapeBlockers(shape: Record<string, unknown>) {
  return Array.isArray(shape.blockers)
    ? shape.blockers.map((value) => String(value).trim()).filter(Boolean)
    : [];
}

function catalogShapeDisabled(shape: Record<string, unknown>) {
  return (
    shape.raw_launchable === false ||
    shape.launchable === false ||
    catalogShapeBlockers(shape).length > 0
  );
}

export function providerCatalogInputOptions(
  field: ProviderRegistryInputField,
  catalog: Record<string, unknown> | undefined,
  values: ProviderInputValues,
): ProviderCatalogOption[] {
  if (!catalog) return [];
  const locations = rows(catalog.locations);
  const shapes = rows(catalog.shapes);
  const subscription = String(values.subscription_id || "").trim();
  const project = String(values.project_id || "").trim();
  const region = String(values.region || "").trim().toLowerCase();
  const zone = String(values.zone || "").trim().toLowerCase();
  const source = String(field.options_source || field.key).toLowerCase();
  const selectedShape = shapes.find(
    (shape) => textValue(shape, "id", "name") === String(values.shape_id || "").trim(),
  );
  const rawShapeCapabilities = selectedShape?.capabilities;
  const shapeCapabilities =
    rawShapeCapabilities &&
    typeof rawShapeCapabilities === "object" &&
    !Array.isArray(rawShapeCapabilities)
      ? (rawShapeCapabilities as Record<string, unknown>)
      : {};
  const locationByID = new Map(
    locations.map((location) => [textValue(location, "id"), location]),
  );
  const locationProject = (location: Record<string, unknown>) => {
    const explicit = textValue(location, "project_id", "project");
    const locationID = textValue(location, "id");
    return explicit || (locationID.includes("/") ? locationID.split("/")[0] : "");
  };
  const shapeLocations = (shape: Record<string, unknown>) =>
    (Array.isArray(shape.location_ids) ? shape.location_ids : [])
      .map((locationID) => locationByID.get(String(locationID)))
      .filter((location): location is Record<string, unknown> => Boolean(location));

  if (field.key === "provisioning_mode") {
    const modes = String(shapeCapabilities.provisioning_modes || "on_demand")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const labels: Record<string, string> = {
      on_demand: "On demand",
      spot: "Spot",
      flex_start: "Flex-start",
      reservation: "Exact reservation",
    };
    return uniqueOptions(modes.map((value) => ({ value, label: labels[value] || value })));
  }

  if (field.key === "boot_disk_type") {
    const diskTypes = String(shapeCapabilities.boot_disk_types || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const labels: Record<string, string> = {
      "pd-standard": "Persistent Disk Standard",
      "pd-balanced": "Persistent Disk Balanced",
      "pd-ssd": "Persistent Disk SSD",
      "hyperdisk-balanced": "Hyperdisk Balanced",
    };
    return uniqueOptions(
      diskTypes.map((value) => ({ value, label: labels[value] || providerTokenLabel(value) })),
    );
  }

  if (source.endsWith("projects") || field.key === "project_id") {
    return uniqueOptions(
      locations
        .map((row) => {
          const explicit = textValue(row, "project_id", "project");
          const value = explicit || locationProject(row);
          return { value, label: value };
        })
        .filter((option) => option.value),
    );
  }

  if (source.endsWith("subscriptions") || field.key === "subscription_id") {
    return uniqueOptions(
      [...locations, ...shapes]
        .map((row) => textValue(row, "subscription_id"))
        .filter(Boolean)
        .map((value) => ({ value, label: value })),
    );
  }
  if (source.endsWith("regions") || field.key === "region") {
    return uniqueOptions(
      locations
        .filter(
          (row) =>
            (!subscription || textValue(row, "subscription_id") === subscription) &&
            (!project || locationProject(row) === project),
        )
        .map((row) => {
          const value = textValue(row, "region", "name");
          return { value, label: textValue(row, "display_name", "name", "region") || value };
        })
        .filter((option) => option.value),
    );
  }
  if (source.endsWith("zones") || field.key === "zone") {
    return uniqueOptions(
      [
        ...locations
          .filter(
            (location) =>
              (!subscription || textValue(location, "subscription_id") === subscription) &&
              (!project || locationProject(location) === project) &&
              (!region || textValue(location, "region").toLowerCase() === region),
          )
          .map((location) => textValue(location, "zone")),
        ...shapes
          .filter(
            (shape) =>
              (!subscription || textValue(shape, "subscription_id") === subscription) &&
              (!project || textValue(shape, "project_id", "project") === project) &&
              (!region || textValue(shape, "region").toLowerCase() === region),
          )
          .flatMap((shape) =>
            Array.isArray(shape.zones) ? shape.zones.map((value) => String(value).trim()) : [],
          ),
      ]
        .filter(Boolean)
        .map((value) => ({ value, label: value }))
        .filter((option) => option.value),
    );
  }
  if (source.endsWith("shapes") || field.key === "shape_id") {
    const compatibleShapes = shapes
      .filter((shape) => {
        const compatibleLocations = shapeLocations(shape);
        if (compatibleLocations.length === 0) {
          const shapeZones = Array.isArray(shape.zones)
            ? shape.zones.map((value) => String(value).trim().toLowerCase())
            : [];
          return (
            (!subscription || textValue(shape, "subscription_id") === subscription) &&
            (!project || textValue(shape, "project_id", "project") === project) &&
            (!region || textValue(shape, "region").toLowerCase() === region) &&
            (!zone ||
              textValue(shape, "zone").toLowerCase() === zone ||
              shapeZones.includes(zone))
          );
        }
        return compatibleLocations.some(
          (location) =>
            (!subscription || textValue(location, "subscription_id") === subscription) &&
            (!project || locationProject(location) === project) &&
            (!region || textValue(location, "region").toLowerCase() === region) &&
            (!zone || textValue(location, "zone", "name").toLowerCase() === zone),
        );
      });
    const launchableShapes = compatibleShapes.filter((shape) => !catalogShapeDisabled(shape));
    const launchableCostBands = assignHardwareCostBands(launchableShapes, catalogShapeHourlyCost);
    const costBandByShape = new Map(
      launchableShapes.map((shape, index) => [
        shape,
        launchableCostBands[index] || "Mid cost",
      ] as const),
    );
    return uniqueOptions(
      compatibleShapes
        .map((shape) => {
          const value = textValue(shape, "id", "name");
          const cpu = Number(shape.vcpus || shape.vcpu || 0);
          const memory = Number(shape.memory_gb || 0);
          const architecture = textValue(shape, "architecture");
          const localStorage = Number(shape.local_storage_gb || 0);
          const hardware = [
            cpu > 0 ? `${cpu} vCPU` : "",
            memory > 0 ? `${memory} GB RAM` : "",
            acceleratorLabel(shape),
            architecture,
            localStorage > 0 ? `${localStorage.toLocaleString()} GB local SSD` : "",
          ]
            .filter(Boolean)
            .join(" · ");
          const blockers = catalogShapeBlockers(shape);
          const disabled = catalogShapeDisabled(shape);
          return {
            value,
            label: [
              textValue(shape, "name", "id") || value,
              hardware,
              hardwareCostBandLabel(costBandByShape.get(shape) || "Mid cost"),
            ].filter(Boolean).join(" · "),
            disabled,
            detail: blockers.join("; "),
          };
        })
        .filter((option) => option.value),
    );
  }

  const sourceRows = source.endsWith("images")
    ? rows(catalog.images)
    : source.endsWith("network_profiles") ||
        source.endsWith("subnets") ||
        source.endsWith("subnetworks")
      ? rows(catalog.networks)
      : source.endsWith("ssh_keys")
        ? rows(catalog.ssh_keys)
        : [];
  const compatibleSourceRows = sourceRows.filter((row) => {
    if (source.endsWith("images")) {
      const requiredArchitecture =
        textValue(selectedShape || {}, "architecture") ||
        String(shapeCapabilities.image_architecture || "").trim();
      const imageArchitecture = textValue(row, "architecture");
      if (
        requiredArchitecture &&
        imageArchitecture &&
        requiredArchitecture.toLowerCase() !== imageArchitecture.toLowerCase()
      ) {
        return false;
      }
    }
    const locationIDs = Array.isArray(row.location_ids)
      ? row.location_ids.map((value) => String(value))
      : [];
    if (locationIDs.length === 0) return true;
    return locationIDs.some((locationID) => {
      const location = locationByID.get(locationID);
      return Boolean(
        location &&
          (!subscription || textValue(location, "subscription_id") === subscription) &&
          (!project || locationProject(location) === project) &&
          (!region || textValue(location, "region").toLowerCase() === region) &&
          (!zone || textValue(location, "zone", "name").toLowerCase() === zone),
      );
    });
  });
  return uniqueOptions(
    compatibleSourceRows
      .map((row) => {
        let value = textValue(row, "id", "name");
        if (source.endsWith("network_profiles")) value = textValue(row, "profile_id", "id", "name");
        if (source.endsWith("subnets") || source.endsWith("subnetworks")) {
          value = textValue(row, "subnetwork_id", "id", "name");
        }
        return { value, label: textValue(row, "name", "id") || value };
      })
      .filter((option) => option.value),
  );
}
